import { ServiceProvider } from '../../services/api.types'
import { ROUTES } from '../Routes/routePaths'
import { IGlobalState } from '../../Root/store/appReducer'
import { store } from '../../index'
import { isNative } from './isNative'
import { isDev } from '../../services/apiUrl'
import { Preferences } from '@capacitor/preferences'

const GoogleBaseUrl = 'https://www.googleapis.com/auth/'
export const GOOGLE_FIT_LEAN_SCOPE = ['fitness.activity.read', 'fitness.location.read']
  .map((i) => `${GoogleBaseUrl}${i}`)
  .join('%20')
export const GOOGLE_FIT_FULL_SCOPE = [
  'fitness.activity.read',
  'fitness.location.read',
  'fitness.body.read',
  'fitness.heart_rate.read',
  'userinfo.profile',
]
  .map((i) => `${GoogleBaseUrl}${i}`)
  .join('%20')

const HuaweiBaseUrl = 'https://www.huawei.com/healthkit/'
const HUAWEI_LEAN_SCOPE = ['step.read'].map((i) => `${HuaweiBaseUrl}${i}`).join('%20')
const HUAWEI_FULL_SCOPE = ['heightweight.read', 'calories.read', 'heartrate.read', 'step.read']
  .map((i) => `${HuaweiBaseUrl}${i}`)
  .join('%20')

export const FITBIT_LEAN_SCOPE = ['activity', 'profile'].join('%20')
export const FITBIT_FULL_SCOPE = ['activity', 'heartrate', 'profile', 'weight'].join('%20')

export const formatIntegrationUrl = (baseUrl: string, params: string[]) => {
  return `${baseUrl}?${params.join('&')}`
}

const getGoogleFitIntegrationUrl = (
  redirectUri: string,
  fullScope?: boolean,
  withDistance?: boolean,
  withSleep?: boolean,
) => {
  const baseUrl = 'https://accounts.google.com/o/oauth2/v2/auth'
  let clientId = '1096179851009-frlrfhhuv84djciitvt6vqh865epom7n.apps.googleusercontent.com'

  if (isDev) {
    clientId = '228906401909-em23j37glcnocgkfck1gkg50i6aovb1m.apps.googleusercontent.com'
  }

  let scope = fullScope ? GOOGLE_FIT_FULL_SCOPE : GOOGLE_FIT_LEAN_SCOPE

  /*if (withDistance) {
    scope = [scope, GoogleBaseUrl + 'fitness.location.read'].join('%20')
    if (fullScope) {
      scope = [scope, GoogleBaseUrl + 'userinfo.profile'].join('%20')
    }
  }*/

  if (withSleep) {
    scope = [scope, GoogleBaseUrl + 'fitness.sleep.read'].join('%20')
  }

  const parameters = [
    'response_type=code',
    `scope=${scope}`,
    'access_type=offline',
    'prompt=consent',
    `client_id=${clientId}`,
    redirectUri,
  ]

  return formatIntegrationUrl(baseUrl, parameters)
}

const getFitBitIntegrationUrl = (redirectUri: string, fullScope?: boolean) => {
  // const clientId: string = window.location.host === 'app.prelive.yumuuv.com' ? '22BD7Q' : '22DK6N'
  const clientId = '22DK6N'
  const parameters = [
    'https://www.fitbit.com/oauth2/authorize',
    `client_id=${clientId}`,
    'response_type=code',
    `scope=${fullScope ? FITBIT_FULL_SCOPE : FITBIT_LEAN_SCOPE}`,
    'expires_in=31536000',
    redirectUri,
  ]
  const baseUrl = 'https://www.fitbit.com/oauth2/authorize'

  return formatIntegrationUrl(baseUrl, parameters)
}

const getPolarIntegrationUrl = (redirecUri: string, fullScope?: boolean) => {
  const baseUrl = 'https://flow.polar.com/oauth2/authorization'
  const host = window.location.host
  let clientId = '3165b8e3-a184-4ab7-8ca0-e91eddfd8745'
  if (host.includes('prelive') || host.includes('staging')) {
    clientId = 'a3c2e4c6-27a9-47ad-b723-d69fd2e1643a'
  } else if (!isNative() && host.includes('localhost')) {
    clientId = 'fc429d6f-8aec-4165-a360-28a3748b2a45'
  }
  const params: Array<string> = ['response_type=code', `client_id=${clientId}`, redirecUri]
  return formatIntegrationUrl(baseUrl, params)
}

const getSuuntoIntegrationUrl = (redirecUri: string, fullScope?: boolean) => {
  const baseUrl = 'https://cloudapi-oauth.suunto.com/oauth/authorize'
  const clientId = '9a16228d-dbfa-4315-bc1e-df1e3c2ff66f'
  const params: Array<string> = ['response_type=code', `client_id=${clientId}`, redirecUri]
  return formatIntegrationUrl(baseUrl, params)
}

const getHuaweiIntegrationUrl = (redirectUri: string, fullScope?: boolean) => {
  const baseUrl = 'https://oauth-login.cloud.huawei.com/oauth2/v3/authorize'

  const parameters = [
    'response_type=code',
    'client_id=104382205',
    `scope=${fullScope ? HUAWEI_FULL_SCOPE : HUAWEI_LEAN_SCOPE}`,
    'access_type=offline',
    redirectUri,
    // TODO: state & nonce
    //'state=state',
    //'nonce=nonce',
  ]

  if (isNative()) {
    parameters.push('display=touch')
  }

  return formatIntegrationUrl(baseUrl, parameters)
}

export const integrationUrl = ({
  type,
  fullScope,
  withDistance,
  withSleep,
}: {
  type: string
  fullScope?: boolean
  withDistance?: boolean
  withSleep?: boolean
}): string => {
  const webProxy = !isNative() ? '/web-proxy' : ''
  let origin =
    window.location.host && !isNative()
      ? `${window.location.origin}${webProxy}${window.location.pathname}`
      : `https://app.yumuuv.com${webProxy}${ROUTES.INTEGRATION_PROXY.to}`
  if (isNative() && isDev) {
    origin = `https://staging.yumuuv.com${ROUTES.INTEGRATION_PROXY.to}`
  }

  const redirectUri = `redirect_uri=${origin}`

  Preferences.set({ key: 'serviceProvider', value: String(type) }).then()
  switch (type) {
    case ServiceProvider.GOOGLE_FIT:
      return getGoogleFitIntegrationUrl(redirectUri, fullScope, withDistance, withSleep)
    case ServiceProvider.FITBIT:
      return getFitBitIntegrationUrl(redirectUri, fullScope)
    case ServiceProvider.POLAR:
      return getPolarIntegrationUrl(redirectUri, fullScope)
    case ServiceProvider.SUUNTO:
      return getSuuntoIntegrationUrl(redirectUri, fullScope)
    case ServiceProvider.HUAWEI:
      return getHuaweiIntegrationUrl(redirectUri, fullScope)
    default:
      return ''
  }
}

export const getFormattedUrl = async (url?: string) => {
  // eslint-disable-next-line prefer-const
  const { value: letServiceProviderString } = await Preferences.get({ key: 'serviceProvider' })
  const typedServiceProviderString = letServiceProviderString as keyof typeof ServiceProvider
  const serviceProvider = ServiceProvider[typedServiceProviderString]

  let redirectUrl, authorizationCode, oauthToken, oauthVerifierValue

  if (url) {
    const formattedUrl = new URL(url)

    redirectUrl = `${window.location.origin}${window.location.pathname}`
    authorizationCode = formattedUrl.searchParams.get('code') || ''
    oauthToken = formattedUrl.searchParams.get('oauth_token') || ''
    oauthVerifierValue = formattedUrl.searchParams.get('oauth_verifier') || ''
  } else {
    const globalState = store.getState() as any as IGlobalState
    // @ts-ignore
    if (globalState.router && globalState.router.location && globalState.router.location.query) {
      // @ts-ignore
      const query = globalState.router.location.query

      redirectUrl = `${window.location.origin}${globalState.router.location.pathname}`
      authorizationCode = query.code || ''
      oauthToken = query.oauth_token || ''
      oauthVerifierValue = query.oauth_verifier || ''
    }
  }

  return { authorizationCode, serviceProvider, redirectUrl, oauthToken, oauthVerifierValue }
}

export const getIntegrationType = () => {
  /*const params = window.location.href.split('?')[1]
  const type = params ? params.replace('type=', '') : 'Unknown'*/

  let type = 'Unknown'
  let fullScope = false
  let withDistance = false
  let withSleep = false
  // Because we now use memoryHistory, not hashHistory, we need to get the location data from the state.
  const globalState = store.getState() as any as IGlobalState
  // @ts-ignore
  if (globalState.router && globalState.router.location && globalState.router.location.query) {
    // @ts-ignore
    type = globalState.router.location.query.type
    fullScope = globalState.router.location.query.scope != 'LEAN'
    withDistance = globalState.router.location.query.d === '1'
    withSleep = globalState.router.location.query.s === '1'
  }

  return { type, fullScope, withDistance, withSleep }
}
